import _objectDestructuringEmpty from "@babel/runtime/helpers/esm/objectDestructuringEmpty";
import { immerable } from 'immer';
import GridNode from './GridNode';
import { DEFAULT_BREAKPOINT_NAME, GRID_NODE_TYPES } from './constants';
import { convertMapOfAvroToMapOfJson, mergeIntoPath } from './utils';
import { ModuleTypename } from '../../__generated__/chirp/com/hubspot/content/grid/layouts/rpc/GridLayoutsRpc';
import { isEmptyObject } from 'ContentUtils/helpers/ObjectHelpers';
import { getNonLayoutStyles } from './styleUtils';
export default class ModuleNode extends GridNode {
  constructor(_ref) {
    let moduleNodeParams = Object.assign({}, (_objectDestructuringEmpty(_ref), _ref));
    super(moduleNodeParams);
    this[immerable] = true;
  }
  getPath() {
    return this._value.path;
  }
  cloneAndChange(moduleNodeParams) {
    const {
      id,
      parentNodeId,
      value,
      treeRef
    } = moduleNodeParams;
    const newNodeParams = {
      id: id !== null && id !== void 0 ? id : this._id,
      type: GRID_NODE_TYPES.MODULE,
      treeRef: treeRef !== null && treeRef !== void 0 ? treeRef : this._treeRef,
      parentNodeId: parentNodeId !== null && parentNodeId !== void 0 ? parentNodeId : this._parentNodeId,
      value: value !== null && value !== void 0 ? value : this._value
    };
    return newNodeParams.treeRef.createNewModule(newNodeParams);
  }
  serializeNode(__options) {
    return Object.assign({}, this.getValue(), {
      __typename: ModuleTypename
    });
  }
  exportNode() {
    return {
      id: this.getId(),
      value: this.getValue(),
      parentNodeId: this.getParentNodeId(),
      childNodeIds: undefined
    };
  }
  mergeIntoValue(partialValue, path) {
    return this.cloneAndChange({
      value: mergeIntoPath(this._value, partialValue, path)
    });
  }
  mergeIntoStylesByPath(partialStyles, path) {
    return this.cloneAndChange({
      value: Object.assign({}, this._value, {
        styles: mergeIntoPath(this.getStyles(), partialStyles, path)
      })
    });
  }
  mergeIntoEditorProvidedStyles(partialStyles) {
    return this.cloneAndChange({
      value: Object.assign({}, this._value, {
        styles: mergeIntoPath(this.getStyles(), partialStyles, ['editorProvidedModuleBreakpointStyles', 'breakpointStyles'])
      })
    });
  }
  updateEditorProvidedStylesForAllBreakpoints(createNewBreakpointStyles) {
    var _this$getStyles$edito;
    const breakpointStyles = (_this$getStyles$edito = this.getStyles().editorProvidedModuleBreakpointStyles) === null || _this$getStyles$edito === void 0 ? void 0 : _this$getStyles$edito.breakpointStyles;
    if (!breakpointStyles || Object.keys(breakpointStyles).length === 0) {
      return this;
    }
    const newBreakpointStyles = {};
    for (const breakpoint of Object.keys(breakpointStyles)) {
      newBreakpointStyles[breakpoint] = createNewBreakpointStyles(breakpoint, Object.assign({}, breakpointStyles[breakpoint]));
    }
    return this.mergeIntoEditorProvidedStyles(newBreakpointStyles);
  }
  mergeIntoStyleFields(partialStyles) {
    return this.cloneAndChange({
      value: Object.assign({}, this._value, {
        styles: mergeIntoPath(this.getStyles(), partialStyles, ['styleFieldsModuleBreakpointStyles', 'breakpointStyles'])
      })
    });
  }
  mergeIntoOldModuleStyles(partialStyles) {
    return this.cloneAndChange({
      value: Object.assign({}, this._value, {
        styles: mergeIntoPath(this.getStyles(), {
          oldModuleStyles: partialStyles
        }, ['oldModuleStyles'])
      })
    });
  }
  hasStyleOverrides(breakpoint = 'default') {
    var _this$getEditorProvid;
    const editorProvidedBreakpointStyles = (_this$getEditorProvid = this.getEditorProvidedBreakpointStyles(breakpoint)) !== null && _this$getEditorProvid !== void 0 ? _this$getEditorProvid : {};
    if (this.isPegboardModule()) {
      const nonLayoutStyles = getNonLayoutStyles(editorProvidedBreakpointStyles);
      return Object.values(nonLayoutStyles).some(Boolean);
    }
    return !isEmptyObject(editorProvidedBreakpointStyles);
  }
  getEditorProvidedBreakpointStyles(breakpoint = 'default') {
    const editorProvidedBreakpointStyles = this.getStyles().editorProvidedModuleBreakpointStyles;
    if (editorProvidedBreakpointStyles && editorProvidedBreakpointStyles.breakpointStyles[breakpoint]) {
      return editorProvidedBreakpointStyles.breakpointStyles[breakpoint];
    }
    return {};
  }
  getStyles() {
    return this.getValue().styles;
  }
  getEffectiveEditorProvidedStyle(style, breakpoint, breakpointsByInheritance) {
    var _this$getStyles$edito2, _editorProvidedBreakp;
    const editorProvidedBreakpointStyles = ((_this$getStyles$edito2 = this.getStyles().editorProvidedModuleBreakpointStyles) === null || _this$getStyles$edito2 === void 0 ? void 0 : _this$getStyles$edito2.breakpointStyles) || {
      default: {}
    };
    const styleAtBreakpoint = (_editorProvidedBreakp = editorProvidedBreakpointStyles[breakpoint]) === null || _editorProvidedBreakp === void 0 ? void 0 : _editorProvidedBreakp[style];
    if (styleAtBreakpoint !== undefined) {
      return {
        value: styleAtBreakpoint,
        inheritedBy: breakpoint
      };
    }
    const parentBreakpoint = breakpointsByInheritance[breakpoint];
    if (parentBreakpoint === undefined) {
      var _editorProvidedBreakp2;
      return {
        value: (_editorProvidedBreakp2 = editorProvidedBreakpointStyles[DEFAULT_BREAKPOINT_NAME]) === null || _editorProvidedBreakp2 === void 0 ? void 0 : _editorProvidedBreakp2[style],
        inheritedBy: DEFAULT_BREAKPOINT_NAME
      };
    }
    return this.getEffectiveEditorProvidedStyle(style, parentBreakpoint, breakpointsByInheritance);
  }
  getFieldOverrides() {
    return convertMapOfAvroToMapOfJson(this.getValue().fieldOverrides);
  }
  getLabel() {
    var _this$getValue$label;
    return (_this$getValue$label = this.getValue().label) !== null && _this$getValue$label !== void 0 ? _this$getValue$label : '';
  }
  getAllDescendantNodeIds() {
    return [];
  }
  getParentNode() {
    if (this.hasParentNode()) {
      return this.getTree().findContainer(this._parentNodeId);
    }
    throw new Error('Node has no parent node');
  }
  isPegboardModule() {
    return this.getParentNode().isPegboardContainer();
  }

  /**
   * Returns true if the module is manually positioned by the user on a pegboard.
   * Note that on a pegboard, modules are always positioned by ManualPosition
   * styles, so this method is meant to check if a module on a pegboard was
   * positioned by a user specifically.
   * @param breakpoint - The breakpoint to check. If not provided, the default breakpoint will be used.
   * @returns boolean True if the module is manually positioned by the user on a pegboard, false otherwise.
   */
  isManuallyPositionedByUserOnPegboard(breakpoint = DEFAULT_BREAKPOINT_NAME) {
    var _this$getStyles$edito3, _this$getStyles$edito4;
    return (_this$getStyles$edito3 = (_this$getStyles$edito4 = this.getStyles().editorProvidedModuleBreakpointStyles) === null || _this$getStyles$edito4 === void 0 || (_this$getStyles$edito4 = _this$getStyles$edito4.breakpointStyles[breakpoint]) === null || _this$getStyles$edito4 === void 0 ? void 0 : _this$getStyles$edito4.isManuallyPositioned) !== null && _this$getStyles$edito3 !== void 0 ? _this$getStyles$edito3 : false;
  }
}